import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DashboardSection } from './DashboardSection';
import { Button } from '../../design-system/components/Button';
import { Heading, Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { AdminAttentionItem, AdminAttentionKind } from '../../types/dashboard';
import {
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  UserPlusIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const Panel = styled(DashboardSection)``;

const PanelTitle = styled(Heading)`
  margin: 0 0 ${spacing[2]} 0 !important;
`;

const PanelSubtitle = styled(Text)`
  display: block;
  margin-bottom: ${spacing[5]};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[4]};
  padding: ${spacing[3]} 0;
  border-bottom: 1px solid ${props => props.theme.colors.borderLight || props.theme.colors.border};
  background: transparent;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
`;

const RowMain = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  min-width: 0;
  flex: 1;
`;

const IconWrap = styled.div<{ $kind: AdminAttentionKind; $hasWork: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${borders.radius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${props => {
    if (!props.$hasWork) return props.theme.colors.surfaceHover;
    switch (props.$kind) {
      case 'payment':
        return `${props.theme.colors.warning}22`;
      case 'changeRequest':
        return `${props.theme.colors.warning}22`;
      case 'registrationPetition':
      case 'memberPetition':
        return `${props.theme.colors.primary}18`;
      default:
        return props.theme.colors.surfaceHover;
    }
  }};
  color: ${props => {
    if (!props.$hasWork) return props.theme.colors.textSecondary;
    switch (props.$kind) {
      case 'payment':
      case 'changeRequest':
        return props.theme.colors.warning;
      default:
        return props.theme.colors.primary;
    }
  }};

  svg {
    width: 22px;
    height: 22px;
  }
`;

const RowText = styled.div`
  min-width: 0;
`;

const RowTitle = styled(Text)`
  display: block;
  font-weight: ${typography.fontWeight.semibold};
`;

const CountLabel = styled(Text)<{ $hasWork: boolean }>`
  display: block;
  margin-top: ${spacing[1]};
  color: ${props =>
    props.$hasWork ? props.theme.colors.text : props.theme.colors.textSecondary};
  font-weight: ${props =>
    props.$hasWork ? typography.fontWeight.medium : typography.fontWeight.normal};
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[5]};
  border-radius: ${borders.radius.md};
  background: ${props => props.theme.colors.success}12;
  color: ${props => props.theme.colors.success};

  svg {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
  }
`;

function kindIcon(kind: AdminAttentionKind) {
  switch (kind) {
    case 'changeRequest':
      return <ClipboardDocumentListIcon />;
    case 'registrationPetition':
      return <UserPlusIcon />;
    case 'memberPetition':
      return <DocumentTextIcon />;
    case 'payment':
      return <CurrencyEuroIcon />;
    default:
      return <ClipboardDocumentListIcon />;
  }
}

interface AdminNeedsAttentionProps {
  items: AdminAttentionItem[];
  totalPendingWork: number;
}

export const AdminNeedsAttention: React.FC<AdminNeedsAttentionProps> = ({
  items,
  totalPendingWork,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Panel>
      <PanelTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
        {t('admin.home.attention.title')}
      </PanelTitle>
      <PanelSubtitle size="sm" color="secondary">
        {t('admin.home.attention.subtitle')}
      </PanelSubtitle>

      {totalPendingWork === 0 ? (
        <EmptyState>
          <CheckCircleIcon aria-hidden />
          <div>
            <Text size="base" weight="semibold" color="text">
              {t('admin.home.attention.emptyTitle')}
            </Text>
            <Text size="sm" color="secondary" style={{ display: 'block', marginTop: spacing[1] }}>
              {t('admin.home.attention.emptyMessage')}
            </Text>
          </div>
        </EmptyState>
      ) : (
        <List>
          {items.map(item => {
            const hasWork = item.count > 0;
            return (
              <Row key={item.id}>
                <RowMain>
                  <IconWrap $kind={item.kind} $hasWork={hasWork}>
                    {kindIcon(item.kind)}
                  </IconWrap>
                  <RowText>
                    <RowTitle size="base" color="text">
                      {item.title}
                    </RowTitle>
                    <CountLabel size="sm" $hasWork={hasWork}>
                      {item.subtitle
                        ? `${t('admin.home.attention.pendingCount', { count: item.count })} · ${item.subtitle}`
                        : t('admin.home.attention.pendingCount', { count: item.count })}
                    </CountLabel>
                  </RowText>
                </RowMain>
                {hasWork && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(item.link)}
                    style={{ flexShrink: 0 }}
                  >
                    {item.actionLabel}
                    <ArrowRightIcon style={{ width: 16, height: 16, marginLeft: 6 }} />
                  </Button>
                )}
              </Row>
            );
          })}
        </List>
      )}
    </Panel>
  );
};

export default AdminNeedsAttention;
